/**
 * ML Testing tool: backend presigned URL + CSV/Excel parsing for /ml-testing
 */
import axios from 'axios';
import * as XLSX from 'xlsx';
import config from '../config';

const PATH_KEYS = ['path', 's3_path', 'image_path', 's3 path', 'image path', 'key', 'Path', 'S3_Path'];

const ML_TESTING_PRESIGNED_ENDPOINT = `${config.API_BASE_URL}/api/reports/get-presigned-url-ml-testing/`;

/**
 * When CSV rows use `bucket/object/key`, strip the bucket if it matches this env value.
 * Full https://…s3…amazonaws.com/… URLs and s3:// URLs do not require this.
 */
const ML_TESTING_DEFAULT_BUCKET = process.env.REACT_APP_ML_TESTING_DEFAULT_BUCKET || '';

/** Allowed image extensions for ML testing (case-insensitive). Paths without these are discarded. */
const IMAGE_EXTENSIONS = /\.(jpe?g|png|webp|bmp|gif)$/i;

/**
 * Resolve bucket + object key for ML testing presign API.
 * Key is never a full https URL string (bucket is separate).
 *
 * - Virtual-hosted URL → bucket from host, key from path
 * - Path-style URL → bucket first path segment, key rest
 * - s3://bucket/key → bucket + key
 * - bucket/key when bucket matches REACT_APP_ML_TESTING_DEFAULT_BUCKET → key only, bucket from env
 * - Plain key (e.g. media/…) → key as-is, bucket from REACT_APP_ML_TESTING_DEFAULT_BUCKET if set
 * @returns {{ bucket: string | null, key: string }}
 */
export function resolveMlTestingS3Location(pathStr) {
  const raw = String(pathStr || '').trim();
  if (!raw) {
    return { bucket: ML_TESTING_DEFAULT_BUCKET || null, key: '' };
  }

  if (raw.startsWith('https://') || raw.startsWith('http://')) {
    try {
      const url = new URL(raw);
      const host = url.hostname.toLowerCase();
      let pathname = url.pathname.replace(/^\//, '');
      try {
        pathname = decodeURIComponent(pathname.replace(/\+/g, ' '));
      } catch (_) {
        /* keep pathname as-is */
      }

      const virtualHosted = host.match(/^(.+?)\.s3(?:\.([a-z0-9-]+))?\.amazonaws\.com$/);
      if (virtualHosted && pathname) {
        return { bucket: virtualHosted[1], key: pathname };
      }

      if (/^s3([.-]|$)/.test(host) && host.includes('amazonaws.com') && pathname) {
        const parts = pathname.split('/').filter(Boolean);
        if (parts.length >= 2) {
          return { bucket: parts[0], key: parts.slice(1).join('/') };
        }
      }
    } catch (_) {
      /* fall through */
    }
    throw new Error(
      'Could not parse an S3 object key from this URL. Use the key only (e.g. media/uuid/left/file.png), not a generic https link.'
    );
  }

  if (raw.startsWith('s3://')) {
    const without = raw.slice(5);
    const firstSlash = without.indexOf('/');
    if (firstSlash === -1) {
      return { bucket: without, key: '' };
    }
    return {
      bucket: without.slice(0, firstSlash),
      key: without.slice(firstSlash + 1),
    };
  }

  if (ML_TESTING_DEFAULT_BUCKET) {
    const prefix = `${ML_TESTING_DEFAULT_BUCKET}/`;
    if (raw.startsWith(prefix)) {
      return { bucket: ML_TESTING_DEFAULT_BUCKET, key: raw.slice(prefix.length) };
    }
  }

  return {
    bucket: ML_TESTING_DEFAULT_BUCKET || null,
    key: raw,
  };
}

/**
 * Object key only (for callers that only need the key).
 */
export function extractS3ObjectKey(pathStr) {
  return resolveMlTestingS3Location(pathStr).key;
}

/**
 * Returns true if the path looks like an image (has an image extension). Used to skip non-image rows.
 */
export function isLikelyImagePath(pathStr) {
  const raw = String(pathStr || '').trim();
  if (!raw) return false;
  const segment = raw.split('/').pop()?.split('?')[0] || raw;
  return IMAGE_EXTENSIONS.test(segment);
}

function extractPresignedUrlFromResponse(data) {
  if (!data || typeof data !== 'object') return null;
  return (
    data.presigned_url ||
    data.presignedUrl ||
    data.url ||
    data.download_url ||
    (data.data && (data.data.presigned_url || data.data.url || data.data.download_url)) ||
    null
  );
}

async function requestMlTestingPresignedUrl(objectKey, bucket) {
  const token = localStorage.getItem('optha-token');
  if (!token) {
    throw new Error('Not logged in. Sign in to download images for ML testing.');
  }
  if (!objectKey) {
    throw new Error('Could not determine S3 object key from path');
  }
  const params = { path: objectKey };
  if (bucket != null && String(bucket).trim() !== '') {
    params.bucket = String(bucket).trim();
  }
  try {
    const { data } = await axios.get(ML_TESTING_PRESIGNED_ENDPOINT, {
      params,
      headers: {
        Authorization: `Token ${token}`,
        Accept: 'application/json',
      },
    });
    const url = extractPresignedUrlFromResponse(data);
    if (!url || typeof url !== 'string') {
      throw new Error('Server did not return a presigned URL');
    }
    return url;
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      const d = e.response.data;
      const msg =
        (typeof d === 'string' && d) ||
        (d && typeof d.detail === 'string' && d.detail) ||
        (d && typeof d.error === 'string' && d.error) ||
        (Array.isArray(d?.detail) && d.detail.map((x) => x?.msg || x).filter(Boolean).join('; ')) ||
        `Presigned URL request failed (${e.response.status})`;
      throw new Error(msg);
    }
    throw e;
  }
}

/**
 * Ask backend for a presigned GET URL. Sends `path` (object key) and `bucket` when known.
 */
export async function getMlTestingPresignedUrl(pathStr) {
  const { key, bucket } = resolveMlTestingS3Location(pathStr);
  return requestMlTestingPresignedUrl(key, bucket);
}

/**
 * Download image via backend presigned URL, return a File for preprocessImage.
 */
export async function fetchImage(pathStr) {
  const raw = String(pathStr || '').trim();
  if (!raw) throw new Error('Empty path');
  const { key: objectKey, bucket } = resolveMlTestingS3Location(raw);
  const presignedUrl = await requestMlTestingPresignedUrl(objectKey, bucket);
  let res;
  try {
    res = await fetch(presignedUrl, { mode: 'cors' });
  } catch (err) {
    throw new Error(`Failed to download image: ${err.message || 'network error'}`);
  }
  if (!res.ok) {
    throw new Error(`Failed to download image (${res.status})`);
  }
  const blob = await res.blob();
  const name = objectKey.split('/').pop()?.split('?')[0] || 'image.jpg';
  return new File([blob], name, { type: blob.type || 'image/jpeg' });
}

/**
 * Extract list of S3 path strings from CSV or Excel file.
 * Looks for columns: path, s3_path, image_path, or first column.
 */
export function parsePathsFromFile(file) {
  return new Promise((resolve, reject) => {
    const name = (file.name || '').toLowerCase();
    const isCsv = name.endsWith('.csv');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        let rows = [];
        if (isCsv) {
          const text = typeof data === 'string' ? data : new TextDecoder().decode(data);
          rows = parseCSV(text);
        } else {
          const wb = XLSX.read(data, { type: 'binary' });
          const firstSheet = wb.Sheets[wb.SheetNames[0]];
          rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });
        }
        const paths = extractPathColumn(rows);
        resolve(paths);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    if (isCsv) reader.readAsText(file);
    else reader.readAsBinaryString(file);
  });
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  return lines.map((line) => {
    const out = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        inQuotes = !inQuotes;
      } else if ((c === ',' && !inQuotes) || (c === '\t' && !inQuotes)) {
        out.push(cur.trim());
        cur = '';
      } else {
        cur += c;
      }
    }
    out.push(cur.trim());
    return out;
  });
}

function extractPathColumn(rows) {
  if (!rows.length) return [];
  const header = rows[0].map((c) => String(c).trim().toLowerCase());
  let colIndex = -1;
  for (const key of PATH_KEYS) {
    colIndex = header.indexOf(key.toLowerCase());
    if (colIndex === -1 && key !== key.toLowerCase()) colIndex = header.indexOf(key);
    if (colIndex >= 0) break;
  }
  if (colIndex < 0) colIndex = 0;
  const paths = [];
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    const val = row[colIndex];
    const path = val != null ? String(val).trim() : '';
    if (path && isLikelyImagePath(path)) paths.push(path);
  }
  return paths;
}

/**
 * Build CSV string from results for download.
 */
export function buildResultsCSV(results) {
  const headers = [
    's3_path',
    'fundus_label',
    'fundus_prob_non_fundus',
    'fundus_prob_fundus',
    'fundus_logit_non_fundus',
    'fundus_logit_fundus',
    'eyes_label',
    'eyes_prob_left',
    'eyes_prob_right',
    'eyes_logit_left',
    'eyes_logit_right',
    'DR_score',
    'DR_grade',
    'DME_score',
    'DME_grade',
    'mahalanobis_distance',
    'error',
  ];
  const fmt = (v) => (v != null && typeof v === 'number' ? v.toFixed(10) : v);
  const fmt2 = (v) => (v != null && typeof v === 'number' ? v.toFixed(6) : v);
  const lines = [headers.join(',')];
  for (const r of results) {
    const row = [
      r.path ?? '',
      r.fundus?.fundus_label ?? '',
      fmt(r.fundus?.fundus_score?.[0]),
      fmt(r.fundus?.fundus_score?.[1]),
      fmt(r.fundus?.fundus_logits?.[0]),
      fmt(r.fundus?.fundus_logits?.[1]),
      r.eyes?.eyes_label ?? '',
      fmt(r.eyes?.eyes_score?.[0]),
      fmt(r.eyes?.eyes_score?.[1]),
      fmt(r.eyes?.eyes_logits?.[0]),
      fmt(r.eyes?.eyes_logits?.[1]),
      fmt(r.drdme?.DR_score),
      r.drdme?.DR_grade ?? '',
      fmt(r.drdme?.DME_score),
      r.drdme?.DME_grade ?? '',
      fmt2(r.drdme?.mahalanobis_distance),
      r.error ?? '',
    ].map((c) => (typeof c === 'string' && (c.includes(',') || c.includes('"')) ? `"${String(c).replace(/"/g, '""')}"` : c));
    lines.push(row.join(','));
  }
  return lines.join('\n');
}

/**
 * Build XLSX blob from results for download.
 */
export function buildResultsExcel(results) {
  const rows = [
    [
      's3_path',
      'fundus_label',
      'fundus_prob_non_fundus',
      'fundus_prob_fundus',
      'fundus_logit_non_fundus',
      'fundus_logit_fundus',
      'eyes_label',
      'eyes_prob_left',
      'eyes_prob_right',
      'eyes_logit_left',
      'eyes_logit_right',
      'DR_score',
      'DR_grade',
      'DME_score',
      'DME_grade',
      'mahalanobis_distance',
      'error',
    ],
  ];
  const fmt = (v) => (v != null && typeof v === 'number' ? v.toFixed(10) : v);
  const fmt2 = (v) => (v != null && typeof v === 'number' ? v.toFixed(6) : v);
  for (const r of results) {
    rows.push([
      r.path ?? '',
      r.fundus?.fundus_label ?? '',
      fmt(r.fundus?.fundus_score?.[0]),
      fmt(r.fundus?.fundus_score?.[1]),
      fmt(r.fundus?.fundus_logits?.[0]),
      fmt(r.fundus?.fundus_logits?.[1]),
      r.eyes?.eyes_label ?? '',
      fmt(r.eyes?.eyes_score?.[0]),
      fmt(r.eyes?.eyes_score?.[1]),
      fmt(r.eyes?.eyes_logits?.[0]),
      fmt(r.eyes?.eyes_logits?.[1]),
      fmt(r.drdme?.DR_score),
      r.drdme?.DR_grade ?? '',
      fmt(r.drdme?.DME_score),
      r.drdme?.DME_grade ?? '',
      fmt2(r.drdme?.mahalanobis_distance),
      r.error ?? '',
    ]);
  }
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'ML Results');
  const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
